import { ButtonDropdown, NavItem, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ModalId } from '../../../../enums/modalId'
import { DemoInvoiceFinalizeAction } from '../../enums/demoInvoiceFinalizeAction'
import { useTrackDemoInvoiceEditor } from '../../hooks/useTrackDemoInvoiceEditor'
import { getPrintableInvoiceHtml } from '../../utils/getPrintableInvoiceHtml'
import { printHtml } from '../../utils/printHtml'

interface DemoInvoiceFinalizeMenuProps {
  htmlFiles?: string[]
  onDownload: () => void
}

export const DemoInvoiceFinalizeMenu = ({ htmlFiles, onDownload }: DemoInvoiceFinalizeMenuProps): ReactElement => {
  const { t } = useTranslation()
  const { trackDemoInvoiceEditor } = useTrackDemoInvoiceEditor()
  const { open: openSendAsEmailModal } = useModal(ModalId.DemoInvoiceSendAsEmailModal)
  const { open: openSendAsEInvoiceModal } = useModal(ModalId.DemoInvoiceSendAsEInvoiceModal)

  const dropdownItems = useMemo(
    (): NavItem<DemoInvoiceFinalizeAction>[] => [
      {
        children: t('external_invoices.editor.side_actions.send_by_email'),
        iconLeft: 'mail',
        id: DemoInvoiceFinalizeAction.SendAsEmail,
        value: DemoInvoiceFinalizeAction.SendAsEmail,
      },
      {
        children: t('external_invoices.editor.side_actions.send_as_e_invoice'),
        iconLeft: 'fileCode',
        id: DemoInvoiceFinalizeAction.SendAsEInvoice,
        value: DemoInvoiceFinalizeAction.SendAsEInvoice,
      },
      {
        children: t('external_invoices.editor.side_actions.download_as_pdf'),
        iconLeft: 'file',
        id: DemoInvoiceFinalizeAction.DownloadAsPdf,
        readonly: !htmlFiles?.length,
        value: DemoInvoiceFinalizeAction.DownloadAsPdf,
      },
    ],
    [htmlFiles?.length, t],
  )

  const handleSelect = useCallback(
    (_id: string, action: DemoInvoiceFinalizeAction) => {
      switch (action) {
        case DemoInvoiceFinalizeAction.SendAsEmail:
          trackDemoInvoiceEditor('Finalize - Send by email clicked')
          openSendAsEmailModal()
          break
        case DemoInvoiceFinalizeAction.SendAsEInvoice:
          trackDemoInvoiceEditor('Finalize - Send as e-invoice clicked')
          openSendAsEInvoiceModal()
          break
        case DemoInvoiceFinalizeAction.DownloadAsPdf:
          if (htmlFiles?.length) {
            trackDemoInvoiceEditor('Download button clicked')
            printHtml(getPrintableInvoiceHtml(htmlFiles))
            onDownload()
          }
          break
      }
    },
    [htmlFiles, onDownload, openSendAsEInvoiceModal, openSendAsEmailModal, trackDemoInvoiceEditor],
  )

  const handleOpen = useCallback(() => {
    trackDemoInvoiceEditor('Finalize button clicked')
  }, [trackDemoInvoiceEditor])

  return (
    <ButtonDropdown dropdownSize="l" items={dropdownItems} onOpen={handleOpen} onSelect={handleSelect}>
      {t('external_invoices.editor.side_actions.finalize')}
    </ButtonDropdown>
  )
}
